"use client"

import { useEffect, useState } from "react"
import Image from "next/image"
import { blobMediaUrl } from "@/lib/blob-url"

// Images rendered inside WhatWeOfferMegaMenu + HowItWorksMegaMenu.
// The width/height MUST match the real menu so next/image generates the same
// srcset — otherwise the browser cache misses when the menu actually opens.
const MEGA_MENU_IMAGES: { src: string; width: number; height: number }[] = [
  // HowItWorksMegaMenu nav-item thumbnails (58×58 in mega-menu.tsx:45-51)
  {
    src: blobMediaUrl("/images/mega-menu/how-it-works.jpg"),
    width: 58,
    height: 58,
  },
  {
    src: blobMediaUrl("/images/mega-menu/health-risks.jpg"),
    width: 58,
    height: 58,
  },
  {
    src: blobMediaUrl("/images/mega-menu/health-report.jpg"),
    width: 58,
    height: 58,
  },
  {
    src: blobMediaUrl("/images/mega-menu/biomarkers.jpg"),
    width: 58,
    height: 58,
  },
  // HowItWorksMegaMenu testimonial video posters (120×150 in mega-menu.tsx:67,75)
  { src: blobMediaUrl("/videos/marco-poster.jpg"), width: 120, height: 150 },
  { src: blobMediaUrl("/videos/lucia-poster.jpg"), width: 120, height: 150 },
  // WhatWeOfferMegaMenu hero images (declared 116×116 in mega-menu.tsx:102,121)
  {
    src: blobMediaUrl("/images/homepage/service-mri-machine.jpeg"),
    width: 116,
    height: 116,
  },
  {
    src: blobMediaUrl("/images/homepage/service-blood-cells.jpeg"),
    width: 116,
    height: 116,
  },
]

/**
 * Warms the browser cache for mega-menu images after the page is idle, so the
 * menu paints instantly the first time a user hovers the navbar.
 *
 * We render hidden next/image components (not a manual <link rel="preload">)
 * so Next generates the exact srcset the real menu will request — otherwise
 * every image would be downloaded twice (raw + optimized).
 *
 * Deferred via requestIdleCallback so it never competes with LCP, and gated
 * on the same ≥1200px breakpoint the mega menu itself uses.
 */
export function MegaMenuPreloader() {
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (!window.matchMedia("(min-width: 1200px)").matches) return

    const ric = window.requestIdleCallback
    const cic = window.cancelIdleCallback
    if (ric) {
      const id = ric(() => setReady(true), { timeout: 3000 })
      return () => cic?.(id)
    }
    const id = window.setTimeout(() => setReady(true), 500)
    return () => window.clearTimeout(id)
  }, [])

  if (!ready) return null

  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        pointerEvents: "none",
        opacity: 0,
        width: 1,
        height: 1,
        overflow: "hidden",
        top: 0,
        left: 0,
      }}
    >
      {MEGA_MENU_IMAGES.map((img) => (
        <Image
          key={img.src}
          src={img.src}
          alt=""
          width={img.width}
          height={img.height}
          loading="eager"
        />
      ))}
    </div>
  )
}
